import React, { useMemo, useState } from 'react';
import { UserPlus, X, Info, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ContactBuyerPreferencesEditor from './ContactBuyerPreferencesEditor';
import { bulkAddBuyers } from '../../../api/contact';

/**
 * Add a whole buyer group to the CRM in one go, with their buying preferences,
 * without a property and without leaving anything in the Database section.
 *
 * Christian Gravias, Monday DOMA 12840979019 (2026-08-22): "Is there a way we can
 * just bulk add the buyers into the CRM and add all there preferences without
 * needing to create it in the database tab? There is so many different buyer
 * groups from years of real estate work ... I want a way we can bulk add all this
 * info as a once off and not have it sitting around in database section and cant
 * bulk add data into a bellfield property for example as they wouldnt have come
 * from that specific property."
 *
 * The two doors that already existed both had a cost he did not want: the property
 * Buyers tab needs a listing these buyers never came through, and the Database
 * import leaves a row behind for every person. This one writes the contact and
 * their buying preferences only, so they are matchable in Buyer Match straight
 * away and the Database section stays clean.
 *
 * Deliberately the SAME preferences form as the single-contact editor and the
 * Database bulk button, so there is one form to learn and one payload shape.
 */
const MODES = [
  {
    id: 'merge',
    label: 'Add to what they already have',
    hint: 'For anyone already in the CRM, ranges widen to cover both and suburbs '
      + 'are added to their list. Nothing they previously told you is lost.',
  },
  {
    id: 'replace',
    label: 'Replace their current preferences',
    hint: 'Every field you set below overwrites what is there now. Use when this '
      + 'group is all after the same thing and the old values are noise.',
  },
];

const BulkAddBuyersModal = ({ onClose, onAdded }) => {
  const [pasted, setPasted] = useState('');
  const [note, setNote] = useState('');
  const [mode, setMode] = useState('merge');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Live count so the agent can sanity check before committing 200 rows. Lines
  // are counted, not parsed: the backend does the real classifying.
  const lineCount = useMemo(
    () => pasted.split('\n').map((l) => l.trim()).filter(Boolean).length,
    [pasted],
  );

  const handleSave = async (preferences) => {
    if (!lineCount) {
      toast.error('Paste your buyer list first, one person per line.');
      return;
    }
    setBusy(true);
    try {
      const res = await bulkAddBuyers({
        paste: pasted,
        preferences,
        mode,
        note: note.trim(),
      });
      const data = res.data || {};
      setResult(data);
      toast.success(data.detail || 'Buyers added.');
      if (typeof onAdded === 'function') onAdded(data);
      // Anything skipped stays on screen so the agent can see which rows need a
      // second look. A clean run closes itself.
      if (!(data.skipped || []).length) onClose();
    } catch (e) {
      const data = e?.response?.data || {};
      toast.error(data.error || data.detail || 'Could not add those buyers.');
    } finally {
      setBusy(false);
    }
  };

  const skipped = result?.skipped || [];

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <UserPlus className="w-4 h-4 text-[#3a99a3] shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">
              Bulk add buyers to the CRM
            </span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="px-4 sm:px-8 pt-5 space-y-4">
            <p className="text-[11px] text-gray-500 bg-[#F0FDFE] border border-[#B7E9EE] rounded-lg p-2.5 flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#3a99a3]" />
              <span>
                These buyers go straight into your contacts with the preferences you
                set below, so they start matching listings in Buyer Match. No
                property is needed and nothing is added to the Database section.
              </span>
            </p>

            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">
                Your buyer list, one person per line
              </label>
              <textarea
                autoFocus
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={7}
                spellCheck={false}
                placeholder={
                  'Jane Smith, jane@example.com, 0400 111 222\n'
                  + 'bob@example.com\n'
                  + 'Tom Jones 0422 333 444'
                }
                className="w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm font-mono shadow-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
              />
              <div className="flex items-start justify-between gap-3 mt-1.5">
                <p className="text-[11px] text-gray-400">
                  Name, email and phone in any order, separated by commas or tabs,
                  so a column out of a spreadsheet pastes straight in. Anyone
                  already in the CRM is matched on their email or phone rather than
                  duplicated. A line with no email and no phone cannot be matched,
                  so it is listed back to you instead of guessed at.
                </p>
                {lineCount > 0 && (
                  <span className="shrink-0 text-[11px] font-bold text-[#3a99a3] whitespace-nowrap">
                    {lineCount} {lineCount === 1 ? 'line' : 'lines'}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-[#374151] block mb-1">
                Where is this group from? (optional)
              </label>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. 2019 Bellfield open home sheets"
                className="h-9 w-full rounded-md border border-gray-300 bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-[#55C5D0]"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Recorded against every buyer in this batch so you can tell later
                where they came from.
              </p>
            </div>

            {/* Mode sits above the form because it changes what saving DOES. */}
            <div className="grid gap-2">
              {MODES.map((m) => (
                <label
                  key={m.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    mode === m.id
                      ? 'border-[#55C5D0] bg-[#F0FDFE]'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="bulk-add-buyers-mode"
                    checked={mode === m.id}
                    onChange={() => setMode(m.id)}
                    className="mt-0.5 w-4 h-4 text-[#55C5D0] focus:ring-[#55C5D0]"
                  />
                  <span className="min-w-0">
                    <span className="block text-[13px] font-bold text-gray-900">{m.label}</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">{m.hint}</span>
                  </span>
                </label>
              ))}
            </div>

            {result && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-1">
                <p className="text-[12px] font-bold text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> {result.detail}
                </p>
                <p className="text-[11px] text-emerald-700">
                  {result.created} new, {result.matched} already in the CRM,
                  {' '}preferences set on {result.preferences_applied}.
                </p>
              </div>
            )}

            {skipped.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5">
                <p className="text-[12px] font-bold text-amber-800 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {skipped.length} could not be added
                </p>
                <ul className="max-h-28 overflow-y-auto text-[11px] text-amber-800 space-y-0.5">
                  {skipped.map((s, i) => (
                    <li key={`${s.input}-${i}`} className="truncate">
                      <span className="font-semibold">{s.input || '(blank)'}</span>: {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {busy && (
              <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding your
                buyers. A long list can take a moment.
              </p>
            )}
          </div>

          <ContactBuyerPreferencesEditor
            onBulkSave={handleSave}
            bulkLabel={
              lineCount
                ? `Applying to the ${lineCount} ${lineCount === 1 ? 'buyer' : 'buyers'} pasted above`
                : 'Paste your buyer list above first'
            }
            saveLabel={
              lineCount
                ? `Add ${lineCount} ${lineCount === 1 ? 'Buyer' : 'Buyers'}`
                : 'Add Buyers'
            }
          />
        </div>
      </div>
    </div>
  );
};

export default BulkAddBuyersModal;
